import * as THREE from 'three';
import { ENV } from '../core/SimConfig.js';
import { DEG2RAD } from '../utils.js';

// Reference dynamic pressure at ENV.referenceAirspeed — used for control/damping normalization.
// Uses sea-level density so control feel stays consistent across altitudes.
const REF_Q = 0.5 * ENV.airDensity * ENV.referenceAirspeed * ENV.referenceAirspeed;

// ISA density ratio σ = ρ(h)/ρ₀ for the troposphere; capped at the tropopause.
function densityRatio(altitude) {
  const h = Math.max(0, Math.min(altitude, ENV.isaCeiling));
  return Math.pow(1 - ENV.isaLapseRate * h, ENV.isaDensityExp);
}

// Reusable vectors to avoid per-frame allocations
const _vAir = new THREE.Vector3();
const _vLocal = new THREE.Vector3();
const _invQuat = new THREE.Quaternion();
const _drag = new THREE.Vector3();
const _liftDir = new THREE.Vector3();
const _liftForce = new THREE.Vector3();
const _inducedDrag = new THREE.Vector3();
const _thrust = new THREE.Vector3();
const _gravityForce = new THREE.Vector3();
const _totalForce = new THREE.Vector3();
const _controlTorque = new THREE.Vector3();
const _dampingTorque = new THREE.Vector3();
const _noseDownTorque = new THREE.Vector3();
const _totalTorque = new THREE.Vector3();
const _forward = new THREE.Vector3();
const _right = new THREE.Vector3();
const _vAirNorm = new THREE.Vector3();
const _omegaLocal = new THREE.Vector3();
const _stabilityTorque = new THREE.Vector3();
const _stallRecoveryTorque = new THREE.Vector3();
const _flapDrag = new THREE.Vector3();
const _flapPitchTorque = new THREE.Vector3();
const _airbrakeDrag = new THREE.Vector3();
const _gearDrag = new THREE.Vector3();
const _slipDrag = new THREE.Vector3();

export default class FlightModel {
  constructor(params) {
    this.mass = params.mass;
    this.wingArea = params.wingArea;
    this.wingSpan = params.wingSpan;
    this.maxThrust = params.maxThrust;
    this.dragCoefficients = params.dragCoefficients;
    this.liftParams = params.liftParams;
    this.controlAuthority = params.controlAuthority;
    this.dampingCoefficients = params.dampingCoefficients;
    this.cgOffset = params.cgOffset;
    this.stabilityCoefficients = params.stabilityCoefficients;
    this.momentOfInertia = params.momentOfInertia;
    this.thrustLapse = params.thrustLapse || 0;
    this.thrustLapseSpeed = params.thrustLapseSpeed || 250;
    this.stallRecoveryMoment = params.stallRecoveryMoment || 0;
    this.stallDampingFactor = params.stallDampingFactor ?? 1;
    this.stallControlFactor = params.stallControlFactor ?? 1;
    this.flapParams = params.flapParams || null;
    this.airbrakeParams = params.airbrakeParams || null;
    this.gearParams = params.gearParams || null;
    this.sideslipDrag = params.sideslipDrag || null;
    this._sinBetaMax = this.sideslipDrag
      ? Math.sin(this.sideslipDrag.maxBetaDeg * DEG2RAD) : 0;

    // Persistent output object — computeForces() runs once per frame and its
    // results are consumed immediately by the caller, so we reuse these vectors
    // instead of cloning fresh ones every physics step. The `forces` sub-object
    // is held by Aircraft.lastForces for same-frame debug viz (ForceViz).
    this._out = {
      force: new THREE.Vector3(),
      torque: new THREE.Vector3(),
      alpha: 0,
      stallAngle: 0,
      forces: {
        thrust: new THREE.Vector3(),
        lift: new THREE.Vector3(),
        drag: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
      },
    };
  }

  computeForces(state, controls, wind) {
    const { position, velocity, quaternion, angularVelocity } = state;
    const { throttle, elevator, ailerons, rudder, flaps, airbrake, gear } = controls;
    const rollScale = controls.rollAuthorityScale ?? 1;

    // 1. Airspeed
    _vAir.copy(velocity).sub(wind);
    const airspeedSq = _vAir.lengthSq();
    const airspeed = Math.sqrt(airspeedSq);

    // Air density falls off with altitude (ISA troposphere)
    const sigma = densityRatio(position.y);
    const rho = ENV.airDensity * sigma;

    // Thrust lapse: airspeed-based ram effect plus density-proportional altitude lapse
    const speedLapse = 1 - this.thrustLapse * (airspeed / this.thrustLapseSpeed);
    const thrustLapseFactor = speedLapse * sigma;

    // 2. Body-local airspeed
    _invQuat.copy(quaternion).invert();
    _vLocal.copy(_vAir).applyQuaternion(_invQuat);

    // 3. Dynamic pressure
    const q = 0.5 * rho * airspeedSq;

    // Gravity always applies
    _gravityForce.set(0, -this.mass * ENV.gravity, 0);

    // Early return if near-zero airspeed
    if (airspeed < 0.1) {
      _totalForce.copy(_gravityForce);
      // Thrust still applies
      _forward.set(0, 0, -1).applyQuaternion(quaternion);
      _thrust.copy(_forward).multiplyScalar(this.maxThrust * throttle * thrustLapseFactor);
      _totalForce.add(_thrust);

      _totalTorque.set(0, 0, 0);
      const out = this._out;
      out.force.copy(_totalForce);
      out.torque.set(0, 0, 0);
      // No meaningful AoA/stall at near-zero airspeed — match the old contract
      // (Aircraft falls back to 0 / 15° when these are falsy).
      out.alpha = 0;
      out.stallAngle = 0;
      out.forces.thrust.copy(_thrust);
      out.forces.lift.set(0, 0, 0);
      out.forces.drag.set(0, 0, 0);
      out.forces.velocity.copy(_vAir);
      return out;
    }

    // 4. Directional drag (body-local)
    const dc = this.dragCoefficients;
    const dlx = _vLocal.x;
    const dly = _vLocal.y;
    const dlz = _vLocal.z;

    const dxCoeff = dlx > 0 ? dc.right : dc.left;
    const dyCoeff = dly > 0 ? dc.up : dc.down;
    const dzCoeff = dlz > 0 ? dc.back : dc.forward;

    _drag.set(
      -Math.sign(dlx) * 0.5 * rho * dlx * dlx * dxCoeff.cd * dxCoeff.area,
      -Math.sign(dly) * 0.5 * rho * dly * dly * dyCoeff.cd * dyCoeff.area,
      -Math.sign(dlz) * 0.5 * rho * dlz * dlz * dzCoeff.cd * dzCoeff.area,
    );
    // Transform drag back to world space
    _drag.applyQuaternion(quaternion);

    // 5. Angle of attack and sideslip (measured from relative wind)
    const alpha = Math.atan2(-_vLocal.y, -_vLocal.z);
    const beta = Math.atan2(_vLocal.x, -_vLocal.z);

    // 6. Lift coefficient (with flap contribution)
    const { clSlope, clMax, stallAngleDeg, zeroLiftAngleDeg } = this.liftParams;
    const fp = this.flapParams;
    const flapFrac = flaps || 0;
    const stallAngle = (stallAngleDeg + (fp ? fp.stallAngleBoostDeg * flapFrac : 0)) * DEG2RAD;
    const zeroLiftAngle = (zeroLiftAngleDeg || 0) * DEG2RAD;
    const effectiveAlpha = alpha - zeroLiftAngle;
    const absAlpha = Math.abs(effectiveAlpha);
    const clMaxFlaps = clMax + (fp ? fp.clBoost * flapFrac : 0);
    let CL;
    if (absAlpha <= stallAngle) {
      CL = clSlope * effectiveAlpha;
      CL = Math.max(-clMaxFlaps, Math.min(clMaxFlaps, CL));
    } else {
      CL = clMaxFlaps * (1 - (absAlpha - stallAngle) / stallAngle);
      CL = Math.max(0, CL);
      CL *= Math.sign(effectiveAlpha);
    }

    // 7. Lift force (perpendicular to velocity, in the wing's lift plane)
    const L = q * this.wingArea * CL;
    _vAirNorm.copy(_vAir).normalize();
    _right.set(1, 0, 0).applyQuaternion(quaternion);
    _liftDir.crossVectors(_right, _vAirNorm);
    const liftDirLen = _liftDir.length();
    if (liftDirLen > 0.01) {
      _liftDir.divideScalar(liftDirLen);
    } else {
      // Degenerate case: velocity parallel to wing — use body up as fallback
      _liftDir.set(0, 1, 0).applyQuaternion(quaternion);
    }
    _liftForce.copy(_liftDir).multiplyScalar(L);

    // 8. Induced drag
    const AR = (this.wingSpan * this.wingSpan) / this.wingArea;
    const CDi = (CL * CL) / (Math.PI * AR * 0.8);
    _inducedDrag.copy(_vAirNorm).multiplyScalar(-q * this.wingArea * CDi);

    // 9. Thrust (with lapse)
    _forward.set(0, 0, -1).applyQuaternion(quaternion);
    _thrust.copy(_forward).multiplyScalar(this.maxThrust * throttle * thrustLapseFactor);

    // 10. Gravity already computed

    // 10b. Flap drag (extra forward-axis drag from deflected flaps)
    _flapDrag.set(0, 0, 0);
    if (fp && flapFrac > 0) {
      const flapCd = fp.cdBoost * flapFrac;
      _flapDrag.copy(_vAirNorm).multiplyScalar(-q * this.wingArea * flapCd);
    }

    // 10c. Airbrake drag (split decelerons acting as speed brakes)
    _airbrakeDrag.set(0, 0, 0);
    const abp = this.airbrakeParams;
    const abFrac = airbrake || 0;
    if (abp && abFrac > 0) {
      _airbrakeDrag.copy(_vAirNorm).multiplyScalar(-q * this.wingArea * abp.cdBoost * abFrac);
    }

    // 10d. Landing gear drag
    _gearDrag.set(0, 0, 0);
    const gp = this.gearParams;
    const gearFrac = gear || 0;
    if (gp && gearFrac > 0) {
      _gearDrag.copy(_vAirNorm).multiplyScalar(-q * this.wingArea * gp.cdBoost * gearFrac);
    }

    // 10e. Sideslip drag (crossflow over the fuselage, opposing the motion)
    _slipDrag.set(0, 0, 0);
    const sd = this.sideslipDrag;
    if (sd) {
      const sinBeta = Math.min(Math.abs(_vLocal.x) / airspeed, this._sinBetaMax);
      _slipDrag.copy(_vAirNorm).multiplyScalar(-q * this.wingArea * sd.cd * sinBeta * sinBeta);
    }

    // 11. Sum forces
    _totalForce.copy(_drag)
      .add(_liftForce)
      .add(_inducedDrag)
      .add(_slipDrag)
      .add(_flapDrag)
      .add(_airbrakeDrag)
      .add(_gearDrag)
      .add(_thrust)
      .add(_gravityForce);

    // 12. Post-stall factor: ramps from 1.0 (at stall angle) to minimum (at 2× stall)
    // Used to reduce control authority and damping in separated flow
    const absAlphaForStall = Math.abs(alpha);
    let stallFactor = 1;
    if (absAlphaForStall > stallAngle) {
      const depth = Math.min((absAlphaForStall - stallAngle) / stallAngle, 1);
      stallFactor = 1 - depth * (1 - this.stallDampingFactor);
    }
    // Control stall factor follows same ramp shape but with its own minimum
    let controlStallFactor = 1;
    if (absAlphaForStall > stallAngle) {
      const depth = Math.min((absAlphaForStall - stallAngle) / stallAngle, 1);
      controlStallFactor = 1 - depth * (1 - this.stallControlFactor);
    }

    // 13. Control torques (body-local, reduced in stall)
    const qNorm = Math.min(q / REF_Q, 1);
    _controlTorque.set(
      elevator * this.controlAuthority.pitch * qNorm * controlStallFactor,
      rudder * this.controlAuthority.yaw * qNorm * controlStallFactor,
      ailerons * this.controlAuthority.roll * rollScale * qNorm * controlStallFactor,
    );
    // Transform to world space
    _controlTorque.applyQuaternion(quaternion);

    // 14. Damping torques (body-local, scaled by dynamic pressure and reduced in stall)
    // Aerodynamic damping arises from differential airflow across the airframe —
    // it must vanish at zero airspeed so ground contact forces can dominate on the runway.
    _omegaLocal.copy(angularVelocity).applyQuaternion(_invQuat);
    _dampingTorque.set(
      -this.dampingCoefficients.pitch * _omegaLocal.x * qNorm * stallFactor,
      -this.dampingCoefficients.yaw * _omegaLocal.y * qNorm * stallFactor,
      -this.dampingCoefficients.roll * _omegaLocal.z * qNorm * stallFactor,
    );
    // Transform to world space
    _dampingTorque.applyQuaternion(quaternion);

    // 15. Nose-down pitching moment (CG offset)
    const liftMag = Math.abs(L);
    // Pitch-down torque about body X axis (right axis)
    _noseDownTorque.copy(_right).multiplyScalar(-this.cgOffset * liftMag);

    // 16. Stall recovery torque — dedicated nose-down moment beyond stall angle
    // Unlike the CG-offset torque (which vanishes with lift), this grows with stall depth
    // and uses airspeed² so it works at moderate speeds but doesn't overpower at high speed
    _stallRecoveryTorque.set(0, 0, 0);
    if (absAlphaForStall > stallAngle && this.stallRecoveryMoment > 0) {
      const excessAlpha = absAlphaForStall - stallAngle;
      // Scale with airspeed but cap so it doesn't dominate at high speed
      const speedScale = Math.min(airspeedSq / (40 * 40), 1);
      const moment = -Math.sign(alpha) * this.stallRecoveryMoment * excessAlpha * speedScale;
      // Apply about body X axis (pitch), then transform to world
      _stallRecoveryTorque.set(moment, 0, 0);
      _stallRecoveryTorque.applyQuaternion(quaternion);
    }

    // 17. Weathervane stability torques (tail surfaces aligning aircraft with airflow)
    const stab = this.stabilityCoefficients;
    const qSb = q * this.wingArea * this.wingSpan;
    _stabilityTorque.set(
      -stab.pitch * qSb * alpha,   // pitch: AoA → nose-down restoring torque
      -stab.yaw * qSb * beta,      // yaw: sideslip → nose-into-wind torque
      0,
    );
    _stabilityTorque.applyQuaternion(quaternion);

    // 17b. Flap pitch moment (nose-down, scales with dynamic pressure)
    _flapPitchTorque.set(0, 0, 0);
    if (fp && flapFrac > 0) {
      const qNormFlap = Math.min(q / REF_Q, 1);
      _flapPitchTorque.set(fp.pitchMoment * flapFrac * qNormFlap, 0, 0);
      _flapPitchTorque.applyQuaternion(quaternion);
    }

    // 18. Sum torques
    _totalTorque.copy(_controlTorque)
      .add(_dampingTorque)
      .add(_noseDownTorque)
      .add(_stallRecoveryTorque)
      .add(_stabilityTorque)
      .add(_flapPitchTorque);

    const out = this._out;
    out.force.copy(_totalForce);
    out.torque.copy(_totalTorque);
    out.alpha = alpha;
    out.stallAngle = stallAngle;
    out.forces.thrust.copy(_thrust);
    out.forces.lift.copy(_liftForce);
    out.forces.drag.copy(_drag).add(_inducedDrag).add(_slipDrag);
    out.forces.velocity.copy(_vAir);
    return out;
  }
}
